/**
 * Filter bar for list pages: debounced search box, arbitrary facet controls
 * (passed as children), a row of active-filter chips and "clear filters".
 *
 * The bar is stateless: pages own the values (in the URL via `useListState`)
 * and pass change handlers down, so filters are bookmarkable.
 */
import { X } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/form";

/** One active filter shown as a removable chip. */
export interface FilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

/** Props for `FilterBar`. */
export interface FilterBarProps {
  /** Search value from the URL. */
  search?: string;
  onSearchChange?: (value: string | undefined) => void;
  searchPlaceholder?: string;
  searchLabel?: string;
  /** Facet controls (selects, checkboxes). */
  children?: ReactNode;
  chips?: FilterChip[];
  onClearAll?: () => void;
  /** Debounce for typed search in ms (Enter commits immediately). */
  debounceMs?: number;
}

/** Debounced text input that commits on pause or Enter. */
export function SearchInput({
  value,
  onChange,
  placeholder,
  label,
  description,
  debounceMs = 300,
}: {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  label?: string;
  description?: string;
  debounceMs?: number;
}): ReactElement {
  const { t } = useTranslation();
  const [text, setText] = useState(value ?? "");
  // Sync from the URL when it changes externally (back button, clear all).
  useEffect(() => {
    setText(value ?? "");
  }, [value]);
  useEffect(() => {
    const committed = value === "" ? undefined : value;
    if ((text || undefined) === committed) {
      return undefined;
    }
    const id = setTimeout(() => onChange(text || undefined), debounceMs);
    return () => clearTimeout(id);
  }, [text, value, onChange, debounceMs]);
  return (
    <Input
      type="search"
      name="search"
      autoComplete="off"
      aria-label={label ?? t("app.search")}
      aria-description={description}
      title={description}
      placeholder={placeholder ?? t("app.search")}
      className="w-full sm:w-64"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          onChange(e.currentTarget.value || undefined);
        }
      }}
    />
  );
}

/** Filter bar layout. */
export function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder,
  searchLabel,
  children,
  chips = [],
  onClearAll,
  debounceMs,
}: FilterBarProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="mb-3 space-y-2" role="search" aria-label={t("app.filters")}>
      <div className="flex flex-wrap items-center gap-2 [&>select]:w-full sm:[&>select]:w-auto">
        {onSearchChange ? (
          <SearchInput
            value={search}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            label={searchLabel}
            debounceMs={debounceMs}
          />
        ) : null}
        {children}
      </div>
      {chips.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {chips.map((c) => (
            <span
              key={c.key}
              className="inline-flex max-w-full items-center gap-1 rounded-sm border bg-card px-2 py-0.5 [overflow-wrap:anywhere]"
            >
              {c.label}
              <button
                type="button"
                aria-label={`${t("app.remove")}: ${c.label}`}
                className="rounded-sm hover:bg-accent"
                onClick={c.onRemove}
              >
                <X className="size-3" aria-hidden />
              </button>
            </span>
          ))}
          {onClearAll ? (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onClearAll}>
              {t("app.clearFilters")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
